"use client";

import Image from "next/image";
import Link from "next/link";
import posthog from "posthog-js";
import React from "react";

import Facebook from "../SVG/Facebook";
import Instagram from "../SVG/Instagram";
import LinkedIn from "../SVG/LinkedIn";
import X from "../SVG/X";
import YouTube from "../SVG/YouTube";

export default function FooterClient() {
    return (
        <footer className="bg-[#00324e] w-full">
            <div className="pt-5 px-20 w-full">
                {/* Main Footer Content */}
                <div className="max-w-[1280px] mx-auto">
                    <div className="mb-10">
                        <h2 className="font-nib-pro text-[#eff9fc] text-[32px] leading-[1.3] tracking-[-0.32px] mb-10">
                            RTHM
                        </h2>

                        <div className="flex gap-24 items-start">
                            {/* Left Section */}
                            <div className="flex-1 max-w-[432px]">
                                <p className="font-suisse text-[#eff9fc] text-[16px] leading-[1.5] mb-10">
                                    RTHM integrates remote clinical medicine and
                                    research with advanced molecular and digital
                                    measurement technologies to bring new
                                    diagnostics and treatments to patients
                                    faster, starting with Long COVID.
                                </p>

                                {/* LegitScript Seal */}
                                <Link
                                    href="https://www.legitscript.com/websites/?checker_keywords=rthm.com"
                                    target="_blank"
                                    className="block w-[107px] h-[115px]"
                                    onClick={() => {
                                        posthog?.capture(
                                            "clicked_legitscript_seal",
                                        );
                                    }}
                                >
                                    <Image
                                        src="https://static.legitscript.com/seals/15482469.png"
                                        alt="LegitScript approved"
                                        width={107}
                                        height={115}
                                        loading="lazy"
                                    />
                                </Link>
                            </div>

                            {/* Navigation Links */}
                            <div className="flex gap-6 max-w-[400px]">
                                <div className="flex flex-col w-[188px]">
                                    <Link
                                        href="#"
                                        className="font-suisse text-[#eff9fc] text-[14px] leading-[1.5] py-2 text-right"
                                    >
                                        About Us
                                    </Link>
                                    <Link
                                        href="#"
                                        className="font-suisse text-[#eff9fc] text-[14px] leading-[1.5] py-2 text-right"
                                    >
                                        Our Services
                                    </Link>
                                    <Link
                                        href="#"
                                        className="font-suisse text-[#eff9fc] text-[14px] leading-[1.5] py-2 text-right"
                                    >
                                        Testimonials
                                    </Link>
                                    <Link
                                        href="/resources"
                                        className="font-suisse text-[#eff9fc] text-[14px] leading-[1.5] py-2 text-right"
                                    >
                                        Blog Posts
                                    </Link>
                                    <Link
                                        href="/support"
                                        className="font-suisse text-[#eff9fc] text-[14px] leading-[1.5] py-2 text-right"
                                    >
                                        FAQs
                                    </Link>
                                </div>
                                <div className="flex flex-col w-[188px]">
                                    <Link
                                        href="/contact-us"
                                        className="font-suisse text-[#eff9fc] text-[14px] leading-[1.5] py-2 text-right"
                                    >
                                        Contact Us
                                    </Link>
                                    <Link
                                        href="#"
                                        className="font-suisse text-[#eff9fc] text-[14px] leading-[1.5] py-2 text-right"
                                    >
                                        Careers
                                    </Link>
                                    <Link
                                        href="#"
                                        className="font-suisse text-[#eff9fc] text-[14px] leading-[1.5] py-2 text-right"
                                    >
                                        Community
                                    </Link>
                                    <Link
                                        href="/support"
                                        className="font-suisse text-[#eff9fc] text-[14px] leading-[1.5] py-2 text-right"
                                    >
                                        Support Center
                                    </Link>
                                    <Link
                                        href="/resources"
                                        className="font-suisse text-[#eff9fc] text-[14px] leading-[1.5] py-2 text-right"
                                    >
                                        Resources
                                    </Link>
                                </div>
                            </div>

                            {/* Right Section */}
                            <div className="flex flex-col gap-6 max-w-[260px]">
                                {/* Social Icons */}
                                <div className="flex gap-3">
                                    {[
                                        "Facebook",
                                        "Instagram",
                                        "Twitter",
                                        "LinkedIn",
                                        "Youtube",
                                    ].map((social) => (
                                        <Link
                                            key={social}
                                            href={
                                                social === "Instagram"
                                                    ? "https://www.instagram.com/rthm_health/"
                                                    : social === "Twitter"
                                                      ? "https://x.com/RTHM_Health"
                                                      : social === "Facebook"
                                                        ? "https://www.facebook.com/HealthRTHM"
                                                        : social === "LinkedIn"
                                                          ? "https://www.linkedin.com/company/rthm-health"
                                                          : "https://www.youtube.com/@RTHM_Health"
                                            }
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={() => {
                                                posthog?.capture(
                                                    `clicked_social_icon`,
                                                    {
                                                        social,
                                                    },
                                                );
                                            }}
                                            className="w-6 h-6 flex items-center justify-center hover:opacity-80 transition-opacity"
                                            aria-label={`Visit RTHM on ${social}`}
                                        >
                                            <span className="sr-only">
                                                Visit RTHM on {social}
                                            </span>
                                            {social === "Instagram" && (
                                                <Instagram />
                                            )}
                                            {social === "Twitter" && <X />}
                                            {social === "Facebook" && (
                                                <Facebook />
                                            )}
                                            {social === "Youtube" && (
                                                <YouTube />
                                            )}
                                            {social === "LinkedIn" && (
                                                <LinkedIn />
                                            )}
                                        </Link>
                                    ))}
                                </div>

                                {/* Newsletter Signup */}
                                <div id="mc_embed_signup" className="w-full">
                                    <form
                                        action="https://rthm.us14.list-manage.com/subscribe/post?u=48ca5f943afe3a05c70d3bd9a&amp;id=99411d3d07&amp;f_id=00d95ce0f0"
                                        method="post"
                                        id="mc-embedded-subscribe-form"
                                        name="mc-embedded-subscribe-form"
                                        target="_blank"
                                        noValidate
                                        className="validate"
                                    >
                                        <div className="bg-[#f7f8f8] rounded-[15px] px-4 py-2 flex items-center gap-6">
                                            <div className="mc-field-group flex-1">
                                                <input
                                                    aria-label="Email"
                                                    type="email"
                                                    name="EMAIL"
                                                    placeholder="Join our mailing list"
                                                    className="font-suisse text-[#929e9e] text-[15px] leading-[1.5] bg-transparent border-none outline-none w-full placeholder:text-[#929e9e]"
                                                    id="mce-EMAIL"
                                                    required
                                                />
                                            </div>
                                            <button
                                                type="submit"
                                                id="mc-embedded-subscribe"
                                                className="w-6 h-6 bg-[#929e9e] rounded flex items-center justify-center hover:bg-[#7a8686] transition-colors"
                                                onClick={() => {
                                                    posthog?.capture(
                                                        "clicked_newsletter_signup",
                                                    );
                                                }}
                                            >
                                                <span className="text-white text-xs">
                                                    →
                                                </span>
                                            </button>
                                        </div>
                                        <div
                                            id="mce-responses"
                                            className="clear text-white text-sm mt-2"
                                        >
                                            <div
                                                className="response"
                                                id="mce-error-response"
                                                style={{ display: "none" }}
                                            ></div>
                                            <div
                                                className="response"
                                                id="mce-success-response"
                                                style={{ display: "none" }}
                                            ></div>
                                        </div>
                                        {/* Hidden input for bot protection */}
                                        <div
                                            aria-hidden="true"
                                            style={{
                                                position: "absolute",
                                                left: "-5000px",
                                            }}
                                        >
                                            <input
                                                type="text"
                                                name="b_48ca5f943afe3a05c70d3bd9a_f61086c5ce"
                                                tabIndex={-1}
                                                value=""
                                                readOnly
                                            />
                                        </div>
                                    </form>
                                </div>

                                {/* Contact Info */}
                                <div className="text-[#eff9fc] text-[14px] leading-[1.5] space-y-1">
                                    <p>
                                        <span className="font-suisse font-semibold">
                                            Phone:
                                        </span>
                                        <a
                                            className="font-suisse"
                                            href="tel:+14085084163"
                                        >
                                            {" "}
                                            (408) 508-4163 (call or text)
                                        </a>
                                    </p>
                                    <p>
                                        <span className="font-suisse font-semibold">
                                            Fax:
                                        </span>
                                        <a
                                            className="font-suisse"
                                            href="tel:+18887891402"
                                        >
                                            {" "}
                                            (888) 789-1402
                                        </a>
                                    </p>
                                    <p>
                                        <span className="font-suisse font-semibold">
                                            Hours:
                                        </span>
                                        <span className="font-suisse">
                                            {" "}
                                            Monday - Friday 9a-5p PT
                                        </span>
                                    </p>
                                    <p>
                                        <span className="font-suisse font-semibold">
                                            Email (clinic):
                                        </span>
                                        <span className="font-suisse">
                                            {" "}
                                            support@rthm.com
                                        </span>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Bottom Section */}
                    <div className="border-t border-[#1a5b77] pt-8">
                        <div className="flex justify-between items-center mb-8">
                            <span className="font-suisse text-[#eff9fc] text-[14px] leading-[1.5]">
                                2261 Market Street #10535, San Francisco, CA
                                94114
                            </span>
                            <div className="flex gap-6 font-suisse text-[#eff9fc] text-[14px] leading-[1.5]">
                                <span className="font-suisse text-[#eff9fc] text-[14px] leading-[1.5]">
                                    Copyright © Rthm, Inc.
                                </span>
                                <Link
                                    href="/legal/privacy-policy"
                                    className="underline hover:text-white transition-colors"
                                    target="_blank"
                                    onClick={() => {
                                        posthog?.capture(
                                            "clicked_privacy_policy",
                                        );
                                    }}
                                >
                                    Privacy Policy
                                </Link>
                                <Link
                                    href="/legal/terms-of-use"
                                    className="underline hover:text-white transition-colors"
                                    target="_blank"
                                    onClick={() => {
                                        posthog?.capture(
                                            "clicked_terms_and_conditions",
                                        );
                                    }}
                                >
                                    Terms and Conditions
                                </Link>
                            </div>
                        </div>
                    </div>

                    {/* Legal Text */}
                    <div className="py-8 text-center">
                        <p className="text-white/80 text-sm font-light font-space leading-relaxed max-w-6xl mx-auto mb-3">
                            The federal Physician Payments Sunshine Act requires
                            that detailed information about payment and other
                            payments of value worth over ten dollars ($10) from
                            manufacturers of drugs, medical devices, and
                            biologics to physicians and teaching hospitals be
                            made available to the public
                        </p>
                    </div>
                </div>
            </div>
        </footer>
    );
}
